import { AddOnTextInput, Button, ErrorMessage, Icon, IconButton, LocationInput, Map, Modal, RangeSlider, Skeleton, TextInput, Toggle, Tooltip, Typography } from "@/components/atoms";
import { useNavigate } from "react-router-dom";
import { useUpdateFixedPricingLocationMutation, useUpdateLocationBasedFixedPricingMutation } from "@/redux/apis/config/trip/tripCost";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ZoneRatesFormData, zoneRatesFormSchema } from "./zoneRatesFormSchema";
import { addToast, getCurrencySymbol } from "@/utils";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { usePrompt } from "@/hooks";
import { useEffect, useMemo, useState } from "react";
import { ManagementCostUnit } from "../StateRates/types";
import { ZoneRatesEditAction, ZoneRatesEditTableData } from "./types";
import { FixedBaseInterval, FixedIntervals, FixedPricingLocation, PricingProfile } from "@/redux/slices/trip/types";
import { generateEditColumns, getManagementCostUnitSymbol, getTotalTripCost, validateRanges } from "./helpers";
import { DataTable } from "@/components/molecules";

interface ZoneRatesEditPanelProps {
  pricingProfile: PricingProfile;
  zone: FixedPricingLocation;
}

export const ZoneRatesEditPanel = ({ pricingProfile, zone }: ZoneRatesEditPanelProps) => {
  const navigate = useNavigate();
  const { baseInterval, intervals, pickupLocation, currency, distanceUnit } = zone;
  const currencySymbol = getCurrencySymbol(currency) || "";
  const [updateZone, { isLoading: isLoadingUpdateZone }] = useUpdateFixedPricingLocationMutation();
  const [updateZoneRates, { isLoading: isLoadingUpdateZoneRates }] = useUpdateLocationBasedFixedPricingMutation();
  const [managementCostUnit, setManagementCostUnit] = useState<ManagementCostUnit>(
    (baseInterval?.managementCostUnit as ManagementCostUnit) ?? "percentage"
  );
  const [selectedRange, setSelectedRange] = useState<number | null>(null);
  const [action, setAction] = useState<ZoneRatesEditAction>(undefined);
  const isLoading = isLoadingUpdateZone || isLoadingUpdateZoneRates;

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isDirty, isSubmitted },
  } = useForm<ZoneRatesFormData>({
    defaultValues: { 
      zonePricingName: pickupLocation.name,
      referencePoint: {
        id: "reference-point-id",
        address: pickupLocation.address,
        coordinates: {
          lat: pickupLocation.latitude,
          lng: pickupLocation.longitude,
        },
      },
      pickUpPointArea: pickupLocation.radius,
      baseInterval: {
        minimumRange: 0,
        maximumRange: baseInterval?.maximumRange || 0,
        intervalCost: baseInterval?.intervalCost || 0,
        managementCost: baseInterval?.managementCost || 0,
        managementCostUnit,
      },
      intervals: intervals.map((interval) => ({
        maximumRange: interval.maximumRange || 0,
        intervalCost: interval.intervalCost || 0,
        managementCost: interval.managementCost || 0,
        managementCostUnit,
      })),
    },
    resolver: zodResolver(zoneRatesFormSchema),
  });
  const { append, remove } = useFieldArray({
    control,
    name: "intervals",
  });
  const values = watch();
  
  const promptWhen = isDirty  && !isSubmitted;
  usePrompt({ when: promptWhen, message: "Changes will not be saved. Do you want to proceed?" });

  useEffect(() => {
    const oldManagementCostUnit = values.baseInterval.managementCostUnit;
    if (managementCostUnit !== oldManagementCostUnit) {
      setValue("baseInterval.managementCost", 0, { shouldDirty: true });
      setValue("baseInterval.managementCostUnit", managementCostUnit, { shouldDirty: true });
      for (let i = 0; i < values.intervals.length; i++) {
        setValue(`intervals.${i}.managementCost`, 0, { shouldDirty: true });
        setValue(`intervals.${i}.managementCostUnit`, managementCostUnit, { shouldDirty: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managementCostUnit]);

  const onSubmit = handleSubmit((data) => {
    const isValid = validateRanges([
      data.baseInterval.minimumRange,
      data.baseInterval.maximumRange,
      ...data.intervals.map((interval) => interval.maximumRange),
    ]);

    if (pricingProfile && zone && isValid) {
      updateZone({
        profileId: pricingProfile.id,
        locationId: zone.id,
        name: data.zonePricingName,
        pickup_address: data.referencePoint.address,
        pickup_latitude: data.referencePoint.coordinates.lat,
        pickup_longitude: data.referencePoint.coordinates.lng,
        pickup_radius: data.pickUpPointArea,
      })
        .unwrap()
        .then(() => {
          return updateZoneRates({
            profileId: pricingProfile.id,
            locationId: zone.id,
            name: data.zonePricingName,
            base_interval: {
              range_from: data.baseInterval.minimumRange,
              range_to: data.baseInterval.maximumRange,
              interval_cost: data.baseInterval.intervalCost * 100,
              management_cost:
                managementCostUnit === "percentage"
                  ? data.baseInterval.managementCost
                  : data.baseInterval.managementCost * 100,
              management_cost_unit: data.baseInterval.managementCostUnit,
              note: "",
            },
            intervals: data.intervals.map((i) => ({
              range_to: i.maximumRange,
              interval_cost: i.intervalCost * 100,
              management_cost:
                managementCostUnit === "percentage"
                  ? i.managementCost
                  : i.managementCost * 100,
              management_cost_unit: i.managementCostUnit,
              note: "",
            })),
          }).unwrap();
        })
        .then(() => {
          addToast("success", `Zone Pricing (${data.zonePricingName}) has been successfully updated`);
          navigate("..");
        })
        .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));
    }
  });

  const handleAddRange = () => {
    append({
      maximumRange:
        values.intervals.length > 0
          ? Number(values.intervals[values.intervals.length - 1].maximumRange) + 1
          : Number(values.baseInterval.maximumRange) + 1,
      intervalCost: 0,
      managementCost: values.baseInterval.managementCost,
      managementCostUnit,
    });
  };

  const handleDeleteRange = (i: number) => {
    setSelectedRange(i);
    setAction("deleteRange");
  };

  const handleConfirmDeleteRange = () => {
    if (selectedRange != null) {
      remove(selectedRange-2);
      setAction(undefined);
    }
  };

  const handleManagementCostUnitToggle = (checked: boolean) => {
    setManagementCostUnit(checked ? "percentage" : "fixed");
    addToast("success", `Management Fee switched to ${checked ? "Percentage" : "Fixed Dollar Value"}`, 5000, { replace: true });
  };

  const generateEditData = (baseInterval: FixedBaseInterval, intervals: FixedIntervals) => {
    const jointIntervals = baseInterval ? [baseInterval, ...intervals] : [];
    const data = jointIntervals.map((_, i) => {
      return {
        rangeIntervals: `Range ${i+1}`,
        range: i > 0 ? (
          <div className="flex flex-col gap-x-2 gap-y-2 md:flex-row lg:flex-row">
            <div className="flex flex-row items-center gap-x-2">
              <AddOnTextInput
                endAddOn={distanceUnit}
                type="number"
                size="sm"
                className="w-[110px] [&_input]:text-right"
                value={i > 1 ? Number(intervals[i - 2].maximumRange) : Number(baseInterval?.maximumRange)}
                disabled
              />
              <Typography variant="small" className="text-neutral-dark-gray">
                -
              </Typography>
            </div>
            <AddOnTextInput
              endAddOn={distanceUnit}
              type="number"
              size="sm"
              className="w-[110px] [&_input]:text-right"
              hasError={!!errors.intervals?.[i-1]?.maximumRange}
              step="any"
              {...register(`intervals.${i-1}.maximumRange`)}
            />
          </div>
        ) : (
          <div className="flex flex-col gap-2 md:flex-row lg:flex-row">
            <div className="flex flex-row items-center gap-x-2">
              <AddOnTextInput
                endAddOn={distanceUnit}
                type="number"
                size="sm"
                className="w-[110px] [&_input]:text-right"
                value={0}
                disabled
              />
              <Typography variant="paragraph">-</Typography>
            </div>
            <AddOnTextInput
              endAddOn={distanceUnit}
              type="number"
              size="sm"
              className="w-[110px] [&_input]:text-right"
              hasError={!!errors.baseInterval?.maximumRange}
              {...register("baseInterval.maximumRange")}
            />
          </div>
        ),
        baseTripCost: i > 0 ? (
          <AddOnTextInput
            startAddOn={`${currency} ${currencySymbol}`}
            type="number"
            size="sm"
            className="w-[175px]"
            hasError={!!errors.intervals?.[i-1]?.intervalCost}
            step="any"
            {...register(`intervals.${i-1}.intervalCost`)}
          />
        ) : (
          <AddOnTextInput
            startAddOn={`${currency} ${currencySymbol}`}
            type="number"
            size="sm"
            className="w-[175px]"
            hasError={!!errors.baseInterval?.intervalCost}
            step="any"
            {...register("baseInterval.intervalCost")}
          />
        ),
        managementFee: i > 0 ? (
          <AddOnTextInput
            endAddOn={getManagementCostUnitSymbol(currencySymbol, intervals[i-1].managementCostUnit)}
            type="number"
            size="sm"
            className="w-[175px]"
            hasError={!!errors.intervals?.[i-1]?.managementCost}
            step="any"
            {...register(`intervals.${i-1}.managementCost`)}
          />
        ) : (
          <AddOnTextInput
            endAddOn={getManagementCostUnitSymbol(currencySymbol, baseInterval?.managementCostUnit)}
            type="number"
            size="sm"
            className="w-[175px]"
            hasError={!!errors.baseInterval?.managementCost}
            step="any"
            {...register("baseInterval.managementCost")}
          />
        ),
        totalTripCost: i > 0 ? getTotalTripCost(
            Number(intervals[i-1]?.intervalCost),
            Number(intervals[i-1]?.managementCost),
            currency,
            intervals[i-1]?.managementCostUnit
          ) : getTotalTripCost(
            Number(baseInterval?.intervalCost),
            Number(baseInterval?.managementCost),
            currency,
            baseInterval?.managementCostUnit
          ),
        action: i > 1 && (
          <div className="flex items-center justify-center">
            <IconButton variant="custom" iconName="Trash" className="text-danger" iconSize="md" onClick={() => handleDeleteRange(i+1)} />
          </div>
        ),
      } as ZoneRatesEditTableData;
    });
    return data;
  };

  const data = useMemo(
    () => generateEditData(values.baseInterval as FixedBaseInterval, values.intervals as FixedIntervals),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values]
  );

  const columns = useMemo(
    () => generateEditColumns(distanceUnit),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  if (isLoading) return <Skeleton />;

  return (
    <form onSubmit={onSubmit} className="flex flex-col">
      <div className="flex flex-col xl:flex-row justify-between gap-4">
        <div className="flex flex-col gap-1">
          <Typography variant="h2">{`${pricingProfile ? `${pricingProfile.name}: ` : ""}Manage Zone Pricing`}</Typography>
          <Typography variant="paragraph" className="text-neutral-dark-gray">Pick a point on the map and set how far around it you want to charge special rates</Typography>
        </div>
        <div className="flex flex-row items-center gap-x-3">
          <div className="flex flex-row items-center gap-x-1.5">
            <div className="flex flex-row items-center gap-x-1.5">
              <Typography variant="action" className="text-neutral-dark-gray">Management Fee ({managementCostUnit === "percentage" ? "%" : currencySymbol})</Typography>
              <Tooltip
                content={`Switch between setting your Management Fee as a percentage (%) or a fixed dollar amount (${currencySymbol}).`}
                placement="bottom"
              >
                <span>
                  <Icon name="InfoCircle" variant="Bold" className="rotate-180 text-neutral-dark-gray" size="sm" />
                </span>
              </Tooltip>
            </div>
            <Toggle
              checked={managementCostUnit === "percentage"}
              onChange={handleManagementCostUnitToggle}
              className={managementCostUnit === "percentage" ? "bg-info" : "!bg-success"}
              size="sm"
            >
              {managementCostUnit === "percentage" ? (
                <Typography variant="paragraph" className="text-info">
                  %
                </Typography>
              ) : (
                <Typography variant="paragraph" className="text-success">
                  {currencySymbol}
                </Typography>
              )}
            </Toggle>
          </div>
        </div>
      </div>
      <div className="flex flex-row rounded-lg bg-white border border-neutral-mid-gray min-h-[344px] mt-4">
        <div className="flex-1">
           <Map className="rounded-b-none lg:rounded-r-none min-h-[344px]" circleRadius={values.pickUpPointArea * 1000} center={values.referencePoint?.coordinates} />
        </div>
        <div className="flex flex-1 flex-col py-6 px-5 gap-y-6">
          <div className="flex flex-col gap-1">
            <Typography variant="action" className="text-neutral-dark-gray">Zone Pricing Name</Typography>
            <TextInput
              placeholder="Enter Zone Pricing Name here"
              maxLength={65}
              hasError={!!errors.zonePricingName}
              {...register("zonePricingName")}
            />
            <ErrorMessage errors={errors} name="zonePricingName" />
          </div>
          <div className="flex flex-col gap-1">
            <Typography variant="action" className="text-neutral-dark-gray">Reference Point</Typography>
            <Controller
              name="referencePoint"
              control={control}
              render={({ field }) => (
                <LocationInput
                  placeholder="Enter Reference Point"
                  renderIcon={() => <Icon name="Location" size="md" variant="Bold" className="text-danger" />}
                  onLocationChange={(value) => field.onChange(value)}
                  hasError={!!errors.referencePoint}
                  defaultValue={pickupLocation.address}
                />
              )}
            />
            <ErrorMessage errors={errors} name="referencePoint" />
          </div>
          <div className="flex flex-col gap-1">
            <div className="flex flex-row items-center gap-x-1.5">
              <Typography variant="action" className="text-neutral-dark-gray">Pick Up Point Area</Typography>
              <Tooltip content="Pricing will be applied if the booking falls within the shaded area" placement="top">
              <span>
                <Icon name="InfoCircle" size="md" className="rotate-180 text-neutral-dark-gray" />
              </span>
            </Tooltip>
            </div>
            <div className="flex flex-row items-center gap-x-2">
              <div className="lg:w-[100px] w-[75px]">
                <Typography variant="h2" className="text-info">
                  {values.pickUpPointArea} {distanceUnit}
                </Typography>
              </div>
              <Controller
                name="pickUpPointArea"
                control={control}
                render={({ field }) => (
                  <RangeSlider
                    className="flex-1"
                    min={0}
                    max={30}
                    step={0.5}
                    onChange={(value) => field.onChange(value)}
                    value={field.value}
                  />
                )}
              />
            </div>
          </div>
        </div>
      </div>
      <DataTable
        columns={columns}
        data={data}
        className="[&_table]:table-auto overflow-x-auto w-full bg-white rounded-b-none mt-4"
      />
      <div className="flex w-full items-center justify-between ring-1 ring-neutral-mid-gray rounded-b-lg bg-white p-3">
        <Button variant="secondary" size="sm" startIcon="Add" onClick={handleAddRange}>Add Range</Button>
      </div>
      <div className="flex flex-row items-center justify-end gap-3 mt-6">
        <Button size="lg" variant="secondary" onClick={() => navigate("..")}>Cancel</Button>
        <Button type="submit" size="lg" variant="primary">Save</Button>
      </div>
      <Modal
        className="w-[85vw] md:w-[400px] lg:w-[400px]"
        open={action === "deleteRange"}
        title={`Delete Range ${selectedRange}`}
        description={`This will delete Range ${selectedRange}. You cannot undo this action.`}
        onClose={() => setAction(undefined)}
        renderButtons={() => (
          <div className="flex flex-row w-full gap-3">
            <Button variant="secondary" size="lg" onClick={() => setAction(undefined)} className="flex-1">
              Cancel
            </Button>
            <Button variant="primary" size="lg" onClick={handleConfirmDeleteRange} className="flex-1 bg-danger border-none hover:bg-danger-dark">
              Delete
            </Button>
          </div>
        )}
      />
    </form>
  );
};
